import { addBearerToken } from '@/plugins/http';
import { addBearerTokenToIam } from '@/plugins/http/iam';
import * as auth from '@/services/auth';
import { isAdminUser } from '@/services/iam';
import { useSnackbar } from '@aleph-alpha/ds-components-vue';
import { type User } from '@aleph-alpha/lib-auth';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authManager } from './plugins/auth';
import { useRouter } from 'vue-router';
import { useProjects } from './composables/useProjects';
import { projectsCache } from './services/project';
import { useCollaboration } from './composables/useCollaboration';
import { useWorkspaceUserList } from './composables/useWorkspaceUserList';

// Admin detection is now handled via Pharia IAM /check_user endpoint
// instead of parsing Zitadel claims from the user profile

export const useUser = defineStore('userStore', () => {
  const user = ref<User | null>(null);
  const privileges = ref<Set<string> | null>(null);
  const isAdminRef = ref<boolean>(false);

  /** Validates the user and updates the store. Returns true if the user is valid, false otherwise.
   * If the user has no valid token, it returns false.
   * If the user has no access to the studio, return true.
   */
  async function validate() {
    user.value = await authManager.getUser();

    if (user.value) {
      addBearerToken(user.value.token_type, user.value.access_token);
      addBearerTokenToIam(user.value.token_type, user.value.access_token);
    }
    privileges.value = new Set();
    const authStatus = await auth.isAuthenticated();
    if (authStatus === 200) privileges.value.add('studio');
    else if (authStatus === 401) return false;

    // Check admin status via Pharia IAM
    try {
      isAdminRef.value = await isAdminUser();
    } catch (error) {
      isAdminRef.value = false;
    }

    return true;
  }

  const hasAccess = computed(() => privileges.value?.has('studio'));
  const isAdmin = computed(() => isAdminRef.value);
  const userFullName = computed(() => {
    if (!user.value?.profile) return 'Unknown';

    if (user.value.profile.given_name || user.value.profile.family_name) {
      return `${user.value.profile.given_name} ${user.value.profile.family_name}`;
    }

    if (user.value.profile.preferred_username) {
      return user.value.profile.preferred_username;
    }

    return `${user.value.profile.email}`;
  });

  const userEmail = computed(() => user.value?.profile.email ?? '');

  return {
    user,
    userFullName,
    userEmail,
    validate,
    privileges,
    hasAccess,
    isAdmin,
  };
});

export const useSnackbarStore = defineStore('snackbarStore', useSnackbar);

export const useSpanToPlayground = defineStore('spanToPlayground', () => {
  const model = ref<string | null>(null);
  const modelSettings = ref<object | null>(null);
  const prompt = ref<string>('');
  const output = ref<string | null>(null);
  const isSpanSet = ref<boolean>(false);

  function reset() {
    model.value = null;
    modelSettings.value = null;
    prompt.value = '';
    output.value = null;
    isSpanSet.value = false;
  }

  return {
    model,
    modelSettings,
    prompt,
    output,
    isSpanSet,
    reset,
  };
});

export const useLineageToTrace = defineStore('lineageToTrace', () => {
  const active = ref<boolean>(false);
  const router = useRouter();
  function goBack() {
    router.back();
  }
  return {
    active,
    goBack,
  };
});

export const useProjectsStore = defineStore('projectsStore', () => {
  const { projects, fetch, ...rest } = useProjects();

  async function refetch() {
    projectsCache.clear();
    await fetch();
  }

  return {
    ...rest,
    projects,
    fetch,
    refetch,
  };
});

export const useCollaborationStore = defineStore('collaborationStore', () => {
  const collaboration = useCollaboration();

  return collaboration;
});

export const useWorkspaceUserListStore = defineStore('workspaceUserListStore', () => {
  const { userList, loading, error } = useWorkspaceUserList();

  return {
    userList,
    loading,
    error,
  };
});

export const useRowExpansionStore = defineStore('rowExpansionStore', () => {
  const expandedRows = ref<Set<string>>(new Set());

  const toggleRowExpansion = (rowId: string) => {
    if (expandedRows.value.has(rowId)) {
      expandedRows.value.delete(rowId);
    } else {
      expandedRows.value.add(rowId);
    }
  };

  const isRowExpanded = (rowId: string) => {
    return expandedRows.value.has(rowId);
  };

  const clearExpandedRows = () => {
    expandedRows.value.clear();
  };

  return {
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,
    clearExpandedRows,
  };
});
